import type {
  CanvasViewportEventTrigger,
  CanvasViewportPanEventTrigger,
  OpenCanvasViewportState,
  PanModeState,
  PanSource,
} from './types';

type OpenCanvasStateSlice = {
  OpenCanvasViewportStore: OpenCanvasViewportState;
  PanModeStore: PanModeState;
};

/**
 * Returns the current viewport state
 */
export const selectViewport = (state: OpenCanvasStateSlice) =>
  state.OpenCanvasViewportStore.viewport;

/**
 * Returns true if the viewport is currently being changed by a continuous
 * pan/zoom transition. Otherwise returns false.
 */
export const selectIsViewportChanging = (state: OpenCanvasStateSlice) =>
  state.OpenCanvasViewportStore.isViewportChanging;

/**
 * Returns the current viewport version.
 * The viewport version is incremented every time a viewport change has settled.
 * Use this for cases where you need to detect when the viewport has stopped
 * changing after a continuous change.
 */
export const selectViewportVersion = (state: OpenCanvasStateSlice) =>
  state.OpenCanvasViewportStore.viewportVersion;

/**
 * Returns the current viewport change session if a continuous viewport change
 * is in progress, or null otherwise. The session contains the action type
 * (pan/zoom/zoom input) and the zoom level at the start of the interaction.
 */
export const selectViewportChangeSession = (state: OpenCanvasStateSlice) =>
  state.OpenCanvasViewportStore.viewportChangeSession ?? null;

/**
 * Returns the current viewport scale
 */
export const selectViewportScale = (state: OpenCanvasStateSlice) => {
  const viewport = selectViewport(state);
  return viewport.k;
};

/**
 * Returns true when pan mode is active from any source. `PanModeStore` is the
 * single source of truth — `activeSources` is a set of contributors
 * (`'space-hold'`, `'middle-mouse-pan-event'`, `'pan-surface-pan-event'`) and
 * pan mode is active whenever the set is non-empty.
 */
export const selectIsPanModeActive = (state: OpenCanvasStateSlice) =>
  state.PanModeStore.activeSources.size > 0;

/**
 * Returns true only when pan mode is active via the spacebar. Used to gate the
 * focused-interactable check on `PanSurface` (we let middle-mouse always show
 * the overlay, but suppress space+drag when an interactable element has focus).
 */
export const selectIsSpacePanActive = (state: OpenCanvasStateSlice) =>
  state.PanModeStore.activeSources.has('space-hold');

/**
 * Returns true when pan mode is active via the middle mouse button from
 * either a Designer surface, or a wheel hold initiated from the iframe
 */
export const selectIsMouseWheelPanActive = (state: OpenCanvasStateSlice) =>
  state.PanModeStore.activeSources.has('middle-mouse-pan-event');

/**
 * Returns true while a `PanSurface`-owned drag is in flight, even if the
 * spacebar source has already been released.
 */
export const selectIsPanSurfaceDragActive = (state: OpenCanvasStateSlice) =>
  state.PanModeStore.activeSources.has('pan-surface-pan-event');

export const selectIsCanvasIframeInitiatedMouseWheelPan = (
  state: OpenCanvasStateSlice
) => state.PanModeStore.isTriggeredFromIframe;

/**
 * Type-narrowing predicate for viewport-change triggers that represent an
 * actual pan. `space-hold` is only a pan source, not an event trigger.
 */
export const isPanTrigger = (
  trigger: CanvasViewportEventTrigger
): trigger is CanvasViewportPanEventTrigger =>
  trigger === 'middle-mouse-pan-event' || trigger === 'pan-surface-pan-event';

/**
 * Suppresses stale wheel inactivity timers while a real pan is active.
 * `space-hold` alone is not enough because it does not change the viewport.
 */
export const shouldSuppressWheelEnded = (
  activeSources: ReadonlySet<PanSource>,
  trigger: CanvasViewportEventTrigger
): boolean =>
  trigger === 'wheel-event' &&
  (activeSources.has('middle-mouse-pan-event') ||
    activeSources.has('pan-surface-pan-event'));
